import React, { useState, useEffect } from 'react';
import { cn } from '../../lib/utils';
import { Button } from './Button';
import { Input } from './Input';
import { Modal, ModalHeader, ModalTitle, ModalContent, ModalFooter } from './Modal';
import ConfirmationDialog from './ConfirmationDialog';
import { useTheme } from '../../contexts/ThemeContext';
import { FilterIcon, XIcon, CopyIcon, TrashIcon, CheckIcon, PencilIcon, ExclamationCircleIcon, CheckCircleIcon, InfoIcon } from './Icons';

const FilterPresets = ({ 
  // Filter related
  currentFilters = {},
  onApplyFilters,
  onPresetsChange,
  
  // Component props
  tableId = 'default-table',
  entityName = 'Table',
  isOpen,
  onClose
}) => {
  const { theme } = useTheme();
  const [presets, setPresets] = useState([]);
  const [showSaveModal, setShowSaveModal] = useState(false);
  const [presetName, setPresetName] = useState('');
  const [presetDescription, setPresetDescription] = useState('');
  const [editingPreset, setEditingPreset] = useState(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [presetToDelete, setPresetToDelete] = useState(null);

  // Load presets from localStorage
  useEffect(() => {
    const savedPresets = localStorage.getItem(`filter-presets-${tableId}`);
    if (savedPresets) {
      try {
        setPresets(JSON.parse(savedPresets));
      } catch (error) {
        console.error('Error loading filter presets:', error);
        setPresets([]);
      }
    }
  }, [tableId]);

  // Save presets to localStorage
  const savePresets = (newPresets) => {
    localStorage.setItem(`filter-presets-${tableId}`, JSON.stringify(newPresets));
    setPresets(newPresets);
    // Notify parent component that presets have changed
    if (onPresetsChange) {
      onPresetsChange();
    }
  };

  // Get current filter configuration
  const getCurrentFilters = () => currentFilters;

  // Apply filter configuration
  const applyFilters = (filters) => {
    if (onApplyFilters) {
      onApplyFilters(filters);
    }
  };

  // Save current filters as a new preset
  const handleSavePreset = () => {
    if (!presetName.trim()) return;
    
    const newPreset = {
      id: editingPreset ? editingPreset.id : Date.now().toString(),
      name: presetName.trim(),
      description: presetDescription.trim(),
      filters: getCurrentFilters(),
      createdAt: editingPreset ? editingPreset.createdAt : new Date().toISOString(),
      updatedAt: new Date().toISOString()
    };

    let updatedPresets;
    if (editingPreset) {
      updatedPresets = presets.map(p => p.id === editingPreset.id ? newPreset : p);
    } else {
      updatedPresets = [...presets, newPreset];
    }

    savePresets(updatedPresets);
    setShowSaveModal(false);
    setPresetName('');
    setPresetDescription('');
    setEditingPreset(null);
  };

  // Delete a preset
  const handleDeletePreset = (presetId) => {
    const updatedPresets = presets.filter(p => p.id !== presetId);
    savePresets(updatedPresets);
    setShowDeleteConfirm(false);
    setPresetToDelete(null);
  };

  // Apply a preset
  const handleApplyPreset = (preset) => {
    applyFilters(preset.filters);
    onClose();
  };

  // Start editing a preset
  const startEditPreset = (preset) => {
    setEditingPreset(preset);
    setPresetName(preset.name);
    setPresetDescription(preset.description || '');
    setShowSaveModal(true);
  };

  // Confirm delete
  const confirmDeletePreset = (preset) => {
    setPresetToDelete(preset);
    setShowDeleteConfirm(true);
  };

  const cancelDelete = () => {
    setShowDeleteConfirm(false);
    setPresetToDelete(null);
  };

  // Default preset management
  const getDefaultPresetId = () => {
    return localStorage.getItem(`default-filter-preset-${tableId}`);
  };

  const setDefaultPreset = (presetId) => {
    if (presetId) {
      localStorage.setItem(`default-filter-preset-${tableId}`, presetId);
    } else {
      localStorage.removeItem(`default-filter-preset-${tableId}`);
    }
  };

  const handleSetAsDefault = (presetId) => {
    setDefaultPreset(presetId);
    // Force re-render by updating presets array (to show/hide default badges)
    setPresets([...presets]);
  };

  const handleRemoveDefault = () => {
    setDefaultPreset(null);
    // Force re-render by updating presets array
    setPresets([...presets]);
  };

  // Check if current filters match a preset
  const isPresetActive = (preset) => {
    return JSON.stringify(preset.filters) === JSON.stringify(getCurrentFilters());
  };

  // Clear all filters
  const handleClearAllFilters = () => {
    applyFilters({});
    onClose();
  };

  // Check if there are active filters
  const hasActiveFilters = () => {
    const filters = getCurrentFilters();
    return Object.entries(filters).some(([key, filterConfig]) => {
      if (filterConfig.type === 'multiSelect') return filterConfig.values && filterConfig.values.length > 0;
      if (filterConfig.type === 'dateRange') return filterConfig.from || filterConfig.to;
      if (filterConfig.type === 'numericRange') return filterConfig.min !== undefined || filterConfig.max !== undefined;
      return filterConfig.value;
    });
  };

  // Get filter summary for display
  const getFilterSummary = (filters) => {
    const activeFilters = Object.entries(filters || {}).filter(([key, filterConfig]) => {
      if (filterConfig.type === 'multiSelect') return filterConfig.values && filterConfig.values.length > 0;
      if (filterConfig.type === 'dateRange') return filterConfig.from || filterConfig.to;
      if (filterConfig.type === 'numericRange') return filterConfig.min !== undefined || filterConfig.max !== undefined;
      return filterConfig.value;
    });
    
    if (activeFilters.length === 0) return 'No filters';
    if (activeFilters.length === 1) return '1 filter';
    return `${activeFilters.length} filters`;
  };

  if (!isOpen) return null;

  return (
    <div className={`absolute top-full left-0 mt-1 ${theme.colors.surface} ${theme.border.light} border rounded-xl ${theme.shadow.lg} min-w-96 z-50 backdrop-blur-md`}>
      {/* Header */}
      <div className={`flex items-center justify-between p-4 border-b ${theme.border.light} ${theme.gradients.subtle}`}>
        <h3 className={`text-sm font-semibold ${theme.text.primary} flex items-center`}>
          <FilterIcon className={`w-4 h-4 mr-2 ${theme.text.secondary}`} />
          {entityName} Filter Presets
        </h3>
        <Button
          variant="ghost"
          size="sm"
          onClick={onClose}
          className={`h-8 w-8 p-0 ${theme.text.secondary} hover:${theme.colors.background} hover:${theme.text.primary} rounded-lg transition-colors`}
          title="Close"
        >
          <XIcon className="w-4 h-4" />
        </Button>
      </div>

      <div className="p-4">
        {/* Quick Actions */}
        <div className="flex items-center gap-2 mb-4">
          <Button
            variant="outline"
            size="sm"
            onClick={() => setShowSaveModal(true)}
            disabled={!hasActiveFilters()}
            className={`text-xs ${!hasActiveFilters() ? 'opacity-50 cursor-not-allowed' : ''} ${theme.colors.backgroundSecondary} ${theme.border.light} hover:${theme.colors.primary} hover:${theme.text.inverse} transition-all duration-200`}
            title={hasActiveFilters() ? "Save current filter combination" : "Apply some filters first to save them"}
          >
            <CopyIcon className="w-3 h-3 mr-1" />
            Save Current Filters
          </Button>
          
          {hasActiveFilters() && (
            <Button
              variant="outline"
              size="sm"
              onClick={handleClearAllFilters}
              className={`text-xs ${theme.colors.danger} ${theme.text.inverse} hover:opacity-90 transition-all duration-200 ${theme.shadow.sm}`}
            >
              <TrashIcon className="w-3 h-3 mr-1" />
              Clear All Filters
            </Button>
          )}
        </div>

        {/* Current Filter Status */}
        {hasActiveFilters() ? (
          <div className={`mb-4 p-3 ${theme.colors.info} ${theme.text.inverse} rounded-lg border ${theme.border.light} ${theme.shadow.sm}`}>
            <div className="flex items-center">
              <CheckCircleIcon className="w-4 h-4 mr-2" />
              <span className="text-sm font-medium">
                Current: {getFilterSummary(getCurrentFilters())}
              </span>
            </div>
          </div>
        ) : (
          <div className={`mb-4 p-3 ${theme.colors.background} rounded-lg border ${theme.border.light}`}>
            <div className={`text-sm ${theme.text.secondary} flex items-center`}>
              <InfoIcon className="w-4 h-4 mr-2" />
              💡 Apply some filters first, then you can save them as a preset
            </div>
          </div>
        )}

        {/* Saved Presets */}
        <div className="space-y-2">
          {presets.length === 0 ? (
            <div className={`text-center py-8 ${theme.text.muted}`}>
              <FilterIcon className={`w-8 h-8 mx-auto mb-2 opacity-50 ${theme.text.secondary}`} />
              <p className={`text-sm ${theme.text.primary} font-medium`}>No saved filter presets</p>
              <p className={`text-xs ${theme.text.muted} mt-1`}>
                Apply some filters and click "Save Current Filters" to create your first preset
              </p>
            </div>
          ) : (
            presets.map(preset => {
              const isDefault = getDefaultPresetId() === preset.id;
              return (
                <div
                  key={preset.id}
                  className={cn(
                    `p-4 border rounded-xl transition-all duration-200 ${theme.shadow.sm} hover:${theme.shadow.default}`,
                    isPresetActive(preset)
                      ? `${theme.colors.primary} ${theme.text.inverse} ${theme.shadow.lg} scale-[1.02]`
                      : isDefault
                      ? `${theme.colors.success} ${theme.text.inverse} ${theme.shadow.md}`
                      : `${theme.colors.surface} ${theme.border.light} hover:${theme.colors.background} hover:scale-[1.01]`
                  )}
                >
                  <div className="flex items-start justify-between">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <h4 className={`text-sm font-semibold truncate ${
                          isPresetActive(preset) || isDefault ? theme.text.inverse : theme.text.primary
                        }`}>
                          {preset.name}
                        </h4>
                        {isPresetActive(preset) && (
                          <span className="inline-flex items-center px-1.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800 dark:bg-blue-900/20 dark:text-blue-400">
                            Active
                          </span>
                        )}
                        {isDefault && (
                          <span className="inline-flex items-center px-1.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800 dark:bg-green-900/20 dark:text-green-400">
                            Default
                          </span>
                        )}
                      </div>
                    
                    {preset.description && (
                      <p className={`text-xs mt-1 truncate ${
                        isPresetActive(preset) || isDefault ? 'text-white/80' : theme.text.secondary
                      }`}>
                        {preset.description}
                      </p>
                    )}
                    
                    <div className={`text-xs mt-2 flex items-center ${
                      isPresetActive(preset) || isDefault ? 'text-white/70' : theme.text.muted
                    }`}>
                      <FilterIcon className="w-3 h-3 mr-1" />
                      {getFilterSummary(preset.filters)} • {new Date(preset.createdAt).toLocaleDateString()}
                    </div>
                  </div>
                  
                  <div className="flex items-center gap-1 ml-2">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleApplyPreset(preset)}
                      className={`h-6 px-2 text-xs flex items-center gap-1 ${
                        isPresetActive(preset) || isDefault 
                          ? 'text-white/80 hover:text-white hover:bg-white/20' 
                          : `${theme.text.primary} hover:${theme.colors.backgroundHover}`
                      }`}
                      disabled={isPresetActive(preset)}
                    >
                      <CheckIcon className="w-3 h-3" />
                      Apply
                    </Button>
                    {!isDefault ? (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleSetAsDefault(preset.id)}
                        className={`h-6 px-2 text-xs flex items-center gap-1 ${
                          isPresetActive(preset) || isDefault
                            ? 'text-yellow-200 hover:text-yellow-100 hover:bg-white/20'
                            : 'text-green-600 hover:text-green-800 dark:text-green-400 dark:hover:text-green-300'
                        }`}
                        title="Set as default preset"
                      >
                        ⭐
                        Default
                      </Button>
                    ) : (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={handleRemoveDefault}
                        className={`h-6 px-2 text-xs flex items-center gap-1 ${
                          isPresetActive(preset) || isDefault
                            ? 'text-yellow-200 hover:text-yellow-100 hover:bg-white/20'
                            : 'text-gray-600 hover:text-gray-800 dark:text-gray-400 dark:hover:text-gray-300'
                        }`}
                        title="Remove as default"
                      >
                        ⭐
                        Unset
                      </Button>
                    )}
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => startEditPreset(preset)}
                      className={`h-6 px-2 text-xs flex items-center gap-1 ${
                        isPresetActive(preset) || isDefault 
                          ? 'text-white/80 hover:text-white hover:bg-white/20' 
                          : `${theme.text.primary} hover:${theme.colors.backgroundHover}`
                      }`}
                    >
                      <PencilIcon className="w-3 h-3" />
                      Edit
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => confirmDeletePreset(preset)}
                      className={`h-6 px-2 text-xs flex items-center gap-1 ${
                        isPresetActive(preset) || isDefault
                          ? 'text-red-300 hover:text-red-100 hover:bg-red-500/20'
                          : 'text-red-600 hover:text-red-800 hover:bg-red-50 dark:text-red-400 dark:hover:text-red-300 dark:hover:bg-red-900/30'
                      }`}
                      title="Delete preset"
                    >
                      <TrashIcon className="w-3 h-3" />
                      Delete
                    </Button>
                  </div>
                </div>
              </div>
              )
            })
          )}
        </div>

        {/* Instructions */}
        <div className="text-xs text-gray-500 dark:text-gray-400 border-t pt-3 mt-4">
          <p>• Filter presets save your current filter combinations</p>
          <p>• Apply filters first, then save them as a preset</p>
          <p>• Click ⭐ to set a preset as default</p>
          <p>• Default presets load automatically when form opens</p>
          <p>• Table layout settings are saved separately</p>
        </div>
      </div>

      {/* Save Preset Modal */}
      {showSaveModal && (
        <Modal isOpen={showSaveModal} onClose={() => setShowSaveModal(false)}>
          <ModalHeader>
            <ModalTitle>
              {editingPreset ? 'Edit Filter Preset' : 'Save Filter Preset'}
            </ModalTitle>
          </ModalHeader>
          <ModalContent>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Preset Name *
                </label>
                <Input
                  value={presetName}
                  onChange={(e) => setPresetName(e.target.value)}
                  placeholder="e.g., Active Vessels Only"
                  className="w-full"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Description (optional)
                </label>
                <Input
                  value={presetDescription}
                  onChange={(e) => setPresetDescription(e.target.value)}
                  placeholder="e.g., Shows only active vessels in operation"
                  className="w-full"
                />
              </div>

              {/* Show what will be saved */}
              <div className="bg-gray-50 dark:bg-gray-800 p-3 rounded border">
                <h4 className="text-sm font-medium text-gray-900 dark:text-white mb-2">
                  Filters to save:
                </h4>
                <div className="text-xs text-gray-600 dark:text-gray-400">
                  {getFilterSummary(getCurrentFilters())}
                </div>
              </div>
            </div>
          </ModalContent>
          <ModalFooter className="gap-3">
            <Button
              variant="outline"
              onClick={() => setShowSaveModal(false)}
            >
              Cancel
            </Button>
            <Button
              onClick={handleSavePreset}
              disabled={!presetName.trim()}
            >
              {editingPreset ? 'Update Preset' : 'Save Preset'}
            </Button>
          </ModalFooter>
        </Modal>
      )}

      {/* Delete Confirmation Dialog */}
      <ConfirmationDialog
        isOpen={showDeleteConfirm}
        onClose={cancelDelete}
        onConfirm={() => handleDeletePreset(presetToDelete?.id)}
        title="Delete Filter Preset"
        message={`Are you sure you want to delete the filter preset "${presetToDelete?.name}"?`}
        details={presetToDelete ? {
          'Name': presetToDelete.name,
          ...(presetToDelete.description && { 'Description': presetToDelete.description }),
          'Filters': getFilterSummary(presetToDelete.filters),
          'Created': new Date(presetToDelete.createdAt).toLocaleDateString()
        } : null}
        warningMessage="This action cannot be undone."
        confirmText="Delete Preset"
        cancelText="Cancel"
        confirmVariant="destructive"
      />
    </div>
  );
};

export default FilterPresets;